import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { Box, CircularProgress, Divider, Stack, type Theme, Tooltip, Typography, useMediaQuery } from '@mui/material';
import dayjs from 'dayjs';
import { useState } from 'react';
import { useSelector } from 'react-redux';
import Header from '/@/components/bayer/header';
import MobileHeader from '/@/components/bayer/header/mobile-header';
import PageContainer from '/@/components/bayer/page-container';
import Button from '/@/components/button';
import RecommendationCommentsButton from '/@/components/recommendation-comments-button';
import RecommendationCommentsDrawer from '/@/components/recommendation-comments-drawer';
import RecommendationShare from '/@/components/recommendation-share';
import Spinner from '/@/components/spinner';
import { useBreadcrumbs } from '/@/hooks/breadcrumbs';
import { useGetUserNode } from '/@/hooks/nodes';
import { useRecommendationCommentsPermissions } from '/@/hooks/recommendation';
import { _ } from '/@/locale';
import { getSessionBrands } from '/@/store/session/selectors';
import type { BlockImageKey } from '/@/views/recommendations/v2/block-image';
import CardCarousel from '/@/views/recommendations/v2/steps/management/card-carousel';
import DensityCurve from '/@/views/recommendations/v2/steps/management/density-curve';
import DensityEnvironmentTable from '/@/views/recommendations/v2/steps/management/density-environment-table';
import { NutrientTable } from '/@/views/recommendations/v2/steps/management/fertilization-tab';
import HybridDensityCard from '/@/views/recommendations/v2/steps/management/hybrid-density-card';
import PhenologyTimeline, { PhenologyLegend } from '/@/views/recommendations/v2/steps/management/phenology-timeline';
import { useManagementData } from '/@/views/recommendations/v2/steps/management/use-management-data';
import { usePhenology } from '/@/views/recommendations/v2/steps/management/use-phenology';
import GeneralData from './general-data';
import { BLOCK_LABELS, useCopyBlockImage } from './use-copy-block-image';
import { useSavedRecommendation } from './use-saved-recommendation';

function Section({
    title,
    subtitle,
    children,
    block,
    onCopy,
    copying,
}: {
    title: string;
    subtitle?: string;
    children: React.ReactNode;
    /** Identifies the block for the copy button's test id; renders the button when set. */
    block?: BlockImageKey;
    onCopy?: () => void;
    copying?: boolean;
}) {
    return (
        <Stack spacing={2}>
            <Stack direction='row' alignItems='flex-start' justifyContent='space-between' spacing={1}>
                <Stack>
                    <Typography variant='h6' fontWeight='bold' color='primary'>
                        {title}
                    </Typography>
                    {subtitle && (
                        <Typography variant='body2' color='primary'>
                            {subtitle}
                        </Typography>
                    )}
                </Stack>
                {block && onCopy && (
                    <Tooltip title={_('Copiar como imagen')} arrow>
                        <span>
                            <Button
                                startIcon={
                                    copying ? (
                                        <CircularProgress size={18} />
                                    ) : (
                                        <ContentCopyIcon fontSize='small' sx={{ color: 'primary.onSurface' }} />
                                    )
                                }
                                onClick={onCopy}
                                disabled={copying}
                                size='small'
                                data-testid={`copy-block-image-${block}`}
                            >
                                {_('Copiar {{block}}', { block: BLOCK_LABELS[block]().toLowerCase() })}
                            </Button>
                        </span>
                    </Tooltip>
                )}
            </Stack>
            {children}
        </Stack>
    );
}

/**
 * Read-only view of a saved seed recommendation — where "Terminar" lands.
 *
 * Every section is the component the wizard's last step renders, driven off the same
 * `activeRecommendation` slice, so the two can't drift. Nothing here is editable.
 */
function RecommendationResultV2({
    recommendationId,
    crop = 'corn',
}: {
    recommendationId: number;
    crop?: 'corn' | 'soy';
}) {
    const { recommendation, parsedRecommendation, field, cropConfig, isReady } = useSavedRecommendation({
        recommendationId,
        crop,
    });
    const { data: grower } = useGetUserNode(recommendation?.userNode as number);
    // The case the legacy seed detail uses, so both pages show one breadcrumb trail.
    const breadcrumbs = useBreadcrumbs('recommendation.RecommendationDetail');
    const isSmOrLower = useMediaQuery((theme: Theme) => theme.breakpoints.down('sm'));
    const [commentsDrawerOpen, setCommentsDrawerOpen] = useState(false);
    const { shouldShowCommentsButton } = useRecommendationCommentsPermissions(
        recommendation?.commentsStatus,
        recommendation?.userNode
    );

    const { hybrids, planterEfficiency, areaInHectares, isLoading } = useManagementData({ field, crop });
    const { hybrids: phenologyHybrids } = usePhenology({ field, crop, plantingDate: cropConfig?.plantingDate });
    const sessionBrands = useSelector(getSessionBrands);
    const showsDensityTable =
        sessionBrands?.[crop]?.[0]?.showDensityChart && hybrids.some((hybrid) => hybrid.densityTableValues.length > 0);
    const { copyBlock, copyingBlock } = useCopyBlockImage(parsedRecommendation);

    const lastUpdated = recommendation?.modified || recommendation?.created;

    const commentsMenuItem = {
        label: _('Ver o dejar comentarios'),
        onClick: () => setCommentsDrawerOpen(true),
        hidden: !(recommendation?.id && shouldShowCommentsButton),
    };

    // The phone's nav bar carries a short title; the full one and the last-updated line sit in
    // the content, above the field card.
    const mobileHeader = (
        <MobileHeader
            title={_('Recomendación')}
            breadcrumbs={breadcrumbs}
            moreButton={
                parsedRecommendation && (
                    <RecommendationShare
                        type={crop}
                        recommendation={parsedRecommendation}
                        version={2}
                        pdfVersion={2}
                        variant='menu'
                        extraMenuItems={[commentsMenuItem]}
                    />
                )
            }
        />
    );

    const header = (
        <Header
            variant='full-gray'
            breadcrumbs={breadcrumbs}
            title={_('Recomendación de híbrido de maíz')}
            subtitle={
                lastUpdated
                    ? _('Última actualización {{date}}', { date: dayjs(lastUpdated).format('DD/MM/YYYY') })
                    : undefined
            }
            actions={[
                // `disabled` covers the loading window; the prop itself isn't optional.
                parsedRecommendation && (
                    <RecommendationShare
                        key='recommendation-share'
                        type={crop}
                        recommendation={parsedRecommendation}
                        version={2}
                        pdfVersion={2}
                    />
                ),
                recommendation?.id && shouldShowCommentsButton && (
                    <RecommendationCommentsButton
                        key='comments-button'
                        commentsStatus={recommendation.commentsStatus}
                        onOpenDrawer={() => setCommentsDrawerOpen(true)}
                    />
                ),
            ].filter(Boolean)}
        />
    );

    if (!isReady) {
        return (
            <PageContainer
                heroHeader={isSmOrLower ? undefined : header}
                header={isSmOrLower ? mobileHeader : undefined}
            >
                <Stack alignItems='center' justifyContent='center' py={10}>
                    <Spinner />
                </Stack>
            </PageContainer>
        );
    }

    return (
        <PageContainer heroHeader={isSmOrLower ? undefined : header} header={isSmOrLower ? mobileHeader : undefined}>
            {/* `MobileHeader` bleeds past the container with a negative margin, whose bottom half
                eats the padding below it — so the phone needs double to clear the nav bar. */}
            <Stack spacing={isSmOrLower ? 3 : 4} pt={isSmOrLower ? 4 : 2} pb={8}>
                {isSmOrLower && (
                    <Stack spacing={1}>
                        <Typography variant='h6' fontWeight='bold' color='primary'>
                            {_('Recomendación de híbrido de maíz')}
                        </Typography>
                        {lastUpdated && (
                            <Typography variant='caption' color='primary.mui.inactive'>
                                {_('Última actualización {{date}}', {
                                    date: dayjs(lastUpdated).format('DD/MM/YYYY'),
                                })}
                            </Typography>
                        )}
                    </Stack>
                )}
                <GeneralData
                    grower={grower}
                    field={field}
                    plantingDate={cropConfig?.plantingDate}
                    environment={cropConfig?.environment}
                    climatePattern={cropConfig?.climatePattern}
                    soilMoisture={cropConfig?.soilMoisture}
                    crop={crop}
                    compact={isSmOrLower}
                />

                <Section
                    title={_('Híbridos recomendados')}
                    subtitle={_('Híbridos recomendados según el ambiente y las condiciones del lote.')}
                >
                    {isLoading && !hybrids.length ? (
                        <Stack alignItems='center' py={6}>
                            <Spinner />
                        </Stack>
                    ) : (
                        <>
                            {/* Too wide to stack on a phone, so they swipe there and grid on desktop. */}
                            {isSmOrLower ? (
                                <CardCarousel>
                                    {hybrids.map((hybrid) => (
                                        <HybridDensityCard
                                            key={hybrid.id}
                                            hybrid={hybrid}
                                            planterEfficiency={planterEfficiency}
                                            areaInHectares={areaInHectares}
                                        />
                                    ))}
                                </CardCarousel>
                            ) : (
                                <Box
                                    display='grid'
                                    gap={2}
                                    gridTemplateColumns={{ sm: 'repeat(2, 1fr)', lg: 'repeat(3, 1fr)' }}
                                    alignItems='stretch'
                                >
                                    {hybrids.map((hybrid) => (
                                        <HybridDensityCard
                                            key={hybrid.id}
                                            hybrid={hybrid}
                                            planterEfficiency={planterEfficiency}
                                            areaInHectares={areaInHectares}
                                        />
                                    ))}
                                </Box>
                            )}
                            <Stack direction='row' spacing={1} alignItems='center'>
                                <InfoOutlinedIcon sx={{ fontSize: 18, color: (t) => t.palette.primary.main }} />
                                <Typography variant='body2' color='primary'>
                                    {_('Densidad de siembra calculada con un porcentaje de logro del {{value}}%.', {
                                        value: planterEfficiency,
                                    })}
                                </Typography>
                            </Stack>
                        </>
                    )}
                </Section>

                <Divider />

                <Section
                    title={_('Densidad objetivo')}
                    subtitle={_(
                        'Cantidad mínima de plantas a cosecha para el máximo rendimiento en el ambiente definido.'
                    )}
                    block='density_curve'
                    onCopy={() => copyBlock('density_curve')}
                    copying={copyingBlock === 'density_curve'}
                >
                    <DensityCurve hybrids={hybrids} />
                </Section>

                {showsDensityTable && (
                    <>
                        <Divider />

                        <Section
                            title={_('Densidad según el ambiente')}
                            subtitle={_('Densidad de siembra recomendada para cada rango de ambiente.')}
                            block='density_table'
                            onCopy={() => copyBlock('density_table')}
                            copying={copyingBlock === 'density_table'}
                        >
                            <DensityEnvironmentTable hybrids={hybrids} />
                        </Section>
                    </>
                )}

                <Divider />

                <Section
                    title={_('Fenología según la fecha de siembra')}
                    subtitle={_('Fechas de cada estadio y período crítico según la fecha de siembra.')}
                    block='phenology'
                    onCopy={() => copyBlock('phenology')}
                    copying={copyingBlock === 'phenology'}
                >
                    <PhenologyTimeline hybrids={phenologyHybrids} />
                    <PhenologyLegend />
                </Section>

                <Divider />

                <Section
                    title={_('Requerimiento nutricional')}
                    subtitle={_(
                        'Nutrientes totales que necesitan los híbridos seleccionados para el rendimiento estimado.'
                    )}
                    block='nutrients'
                    onCopy={() => copyBlock('nutrients')}
                    copying={copyingBlock === 'nutrients'}
                >
                    <NutrientTable hybrids={hybrids} />
                </Section>
            </Stack>

            {recommendation?.id && (
                <RecommendationCommentsDrawer
                    open={commentsDrawerOpen}
                    setCommentsDrawerOpen={setCommentsDrawerOpen}
                    recommendationId={recommendation.id}
                />
            )}
        </PageContainer>
    );
}

export default RecommendationResultV2;
